import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect } from 'react';
import './MerxandatgeItem.css';
import { getPriceDisplay } from './merxandatgeUtils';

// Typical size order for sorting
const TYPICAL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Helper function to sort sizes according to typical order
const sortSizes = (sizes) => {
  if (!sizes || sizes.length === 0) return [];
  
  return [...sizes].sort((a, b) => {
    const indexA = TYPICAL_SIZES.indexOf(a.toUpperCase());
    const indexB = TYPICAL_SIZES.indexOf(b.toUpperCase());
    
    // If both are in the typical list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the list, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the list, it comes first
    if (indexB !== -1) return 1;
    // If neither is in the list, sort alphabetically
    return a.localeCompare(b);
  });
};

// Helper function to calculate time remaining until deadline
const calculateTimeRemaining = (deadlineString) => {
  if (!deadlineString) return null;

  const deadline = new Date(deadlineString);
  if (Number.isNaN(deadline.getTime())) return null;

  const now = new Date();
  const diff = deadline - now;

  if (diff <= 0) return null; // Deadline has passed

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(' ') : null;
};

// Helper to check if the deadline has already passed
const hasDeadlinePassed = (deadlineString) => {
  if (!deadlineString) return false;

  const deadline = new Date(deadlineString);
  if (Number.isNaN(deadline.getTime())) return false;

  return deadline < new Date();
};

function MerxandatgeItem({ socket, item, isAdmin, onEdit, onDelete, onManageStock, onToggleVisibility, hasVariantsWithoutStock }) {
    useSocketReconnect(socket, () => {
        if (item.id) socket.emit('.get_merxandatge_images', item.id);
    });

  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [images, setImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const deadlinePassed = item.type === 'made_to_order' && hasDeadlinePassed(item.next_round_deadline);

  const formatRoundDate = (dateString) => {
    if (!dateString) return null;

    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return null;

    return parsed.toLocaleString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (item.type === 'made_to_order' && item.next_round_deadline && item.round_open) {
      // Calculate initial time remaining
      setTimeRemaining(calculateTimeRemaining(item.next_round_deadline));

      // Update every minute
      const interval = setInterval(() => {
        setTimeRemaining(calculateTimeRemaining(item.next_round_deadline));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    } else {
      setTimeRemaining(null);
    }
  }, [item.next_round_deadline, item.round_open, item.type]);

  useEffect(() => {
    // Request images for the item
    if (item.id) {
      setLoading(true);
      socket.emit('.get_merxandatge_images', item.id);
      
      // Listen for the response
      const handleImages = (data) => {
        if (data.itemId === item.id && Array.isArray(data.images)) {
          setImages(data.images || []);
          setLoading(false);
        }
      };
      
      socket.on('.merxandatge_images', handleImages);
      
      return () => {
        socket.off('.merxandatge_images', handleImages);
      };
    }
  }, [item.id, socket]);

  // Initialize quantity to 1 or maximum available stock if less than 1
  useEffect(() => {
    if (item && typeof item.available_stock !== 'undefined') {
      const maxStock = Math.max(0, item.available_stock || 0);
      setQuantity(Math.min(1, maxStock));
    }
  }, [item]);

  const handleAddToCart = () => {
    socket.emit('.add_to_cart', { 
      itemId: item.id, 
      quantity
    });
    setAddedToCart(true);
    setTimeout(() => {
      setAddedToCart(false);
    }, 2000);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    const maxStock = Math.max(0, item.available_stock || 0);
    if (value > 0 && value <= maxStock) {
      setQuantity(value);
    }
  };

  const handleNextImage = () => {
    setActiveImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrevImage = () => {
    setActiveImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const getActiveImageUrl = () => {
    if (images.length > 0 && images[activeImageIndex]) {
      console.log("Active image URL:", images[activeImageIndex].url);
      return images[activeImageIndex].url;
    }
    return item.image_url || '/default-merchandise.png';
  };

  const isOutOfStock = () => {
    if (item.kind === 'pack') return !(item.total_stock > 0);
    if (item.type === 'made_to_order') return false;
    if (!item.variants || item.variants.length === 0) {
      return (item.available_stock || 0) <= 0;
    }
    return !item.variants.some(v => (v.stock || 0) > 0);
  };

  const renderVariantsTable = () => {
    if (!item.variants || item.variants.length === 0) {
      return <p>No hi ha variants disponibles</p>;
    }

    // Group variants by size for easier display
    const variantsBySize = {};
    const allColors = [...new Set(item.variants.map(v => v.color || "Sense color"))];
    
    item.variants.forEach(variant => {
      const size = variant.size || "Sense talla";
      const color = variant.color || "Sense color";
      
      if (!variantsBySize[size]) {
        variantsBySize[size] = {};
      }
      variantsBySize[size][color] = variant.stock || 0;
    });

    // Sort sizes for display
    const sortedSizes = sortSizes(Object.keys(variantsBySize));

    return (
      <div className="variants-table-container">
        <table className="variants-table">
          <thead>
            <tr>
              <th>Talla / Color</th>
              {allColors.map(color => (
                <th key={color}>{color}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedSizes.map(size => (
              <tr key={size}>
                <td>{size}</td>
                {allColors.map(color => {
                  const stock = variantsBySize[size][color] || 0;
                  return (
                    <td 
                      key={`${size}-${color}`} 
                      className={stock <= 0 ? 'out-of-stock-cell' : 'in-stock-cell'}
                    >
                      {stock}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Update the handleToggleVisibility function to use the prop
  const handleToggleVisibility = () => {
    if (isTogglingVisibility) return;
    
    setIsTogglingVisibility(true);
    
    // Use the prop instead of emitting the event directly
    if (onToggleVisibility) {
      onToggleVisibility();
    }
    
    // Reset the toggling state after a short delay
    setTimeout(() => {
      setIsTogglingVisibility(false);
    }, 500);
  };

  return (
    <div className={`merxandatge-item ${!item.active ? 'inactive-item' : ''} ${deadlinePassed ? 'deadline-passed-item' : ''}`}>
      {/* If the item is not active, show a label */}
      {!item.active && isAdmin && (
        <div className="inactive-label">
          No visible
        </div>
      )}
      <div className="item-image">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <img className={deadlinePassed ? 'deadline-passed-image' : ''} src={getActiveImageUrl()} alt={item.name} />
            {deadlinePassed && (
              <div className="deadline-overlay">Data límit passada</div>
            )}
            {images.length > 1 && (
              <div className="image-navigation">
                <button className="nav-button prev" onClick={handlePrevImage}>
                  &lt;
                </button>
                <div className="image-dots">
                  {images.map((_, index) => (
                    <span 
                      key={index} 
                      className={`dot ${index === activeImageIndex ? 'active' : ''}`}
                      onClick={() => setActiveImageIndex(index)}
                    ></span>
                  ))}
                </div>
                <button className="nav-button next" onClick={handleNextImage}>
                  &gt;
                </button>
              </div>
            )}
            {isOutOfStock() && (
              <div className="out-of-stock-overlay">
                Estoc esgotat
              </div>
            )}
          </>
        )}
      </div>
      <div className="item-details">
        <div className="item-header">
          <h3 className="item-title">{item.name}</h3>

          {item.type === 'made_to_order' && (item.round_open || item.next_round_deadline) && (
            <div className={`round-status ${item.round_open ? 'round-open' : 'round-closed'} ${deadlinePassed ? 'deadline-passed-chip' : ''}`}>
              {item.round_open && timeRemaining ? (
                <>
                  <i className="fas fa-clock" style={{ marginRight: '6px' }}></i>
                  {timeRemaining}
                </>
              ) : item.round_open ? (
                <>
                  <i className="fas fa-clock" style={{ marginRight: '6px' }}></i>
                  {formatRoundDate(item.next_round_deadline) || 'Properament'}
                </>
              ) : (
                <>
                  Obre: {formatRoundDate(item.next_round_deadline) || 'Properament'}
                </>
              )}
            </div>
          )}
        </div>
        <p className="item-description">{item.description}</p>
        <div className="item-price">{getPriceDisplay(item)}</div>

        {/* Show variants information if available and not made_to_order */}
        {item.type !== 'made_to_order' && item.variants && item.variants.length > 0 && (
          <div className="item-variants-info">
            {item.sizes && item.sizes.length > 0 && (
              <div className="variant-type">Talles: {sortSizes(item.sizes).join(', ')}</div>
            )}
            {item.colors && item.colors.length > 0 && (
              <div className="variant-type">Colors: {item.colors.join(', ')}</div>
            )}
            {(!item.sizes || item.sizes.length === 0) && (!item.colors || item.colors.length === 0) && (
              <div className="variant-type">Article sense talles ni colors</div>
            )}
          </div>
        )}

        {item.kind === 'pack' && <div className="variant-info"><span className="pack-label">Pack</span>{(item.pack_components || []).map(component => <div key={component.itemId}>{component.quantity} × {component.product?.name || 'Article no disponible'}</div>)}</div>}

        {/* Show variants table by default if variants exist and not made_to_order */}
        {item.type !== 'made_to_order' && item.variants && item.variants.length > 0 && (
          <div className="view-variants">
            {renderVariantsTable()}
          </div>
        )}

        {!isAdmin && item.kind !== 'pack' && item.type !== 'made_to_order' && !isOutOfStock() && (
          <div className="purchase-controls">
            <input
              type="number"
              min="1"
              max={item.available_stock || 0}
              value={quantity}
              onWheel={(event) => event.currentTarget.blur()}
              onChange={handleQuantityChange}
            />
            <button 
              className={`add-to-cart-button ${addedToCart ? 'added' : ''}`}
              onClick={handleAddToCart}
              disabled={addedToCart}
            >
              {addedToCart ? 'Afegit!' : 'Afegir al carro'}
            </button>
          </div>
        )}

        {!isAdmin && item.type === 'made_to_order' && (
          <div className="preorder-controls">
            <div className="preorder-banner">
              {item.round_open ? (
                <>
                  <span>Demana abans de {item.next_round_deadline ? new Date(item.next_round_deadline).toLocaleString() : '—'}</span>
                  <button
                    className="add-to-cart-button"
                    onClick={() => {
                      socket.emit('.get_merxandatge_item', item.id);
                    }}
                  >
                    Afegir a la comanda
                  </button>
                </>
              ) : (
                <span>Comandes tancades</span>
              )}
            </div>
            <div className="preorder-chip">Sota comanda</div>
          </div>
        )}

        {isAdmin && (
          <div className="admin-item-controls">
            <button 
              onClick={onEdit} 
              className="edit-button"
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500'
              }}
            >
              Editar
            </button>
            <button onClick={onManageStock} className="stock-button" style={{ position: 'relative' }}>
              {item.kind === 'pack' ? 'Articles del pack' : item.type === 'made_to_order' ? 'Data límit' : 'Estoc'}
              {item.type !== 'made_to_order' && hasVariantsWithoutStock && (
                <span className="stock-button-badge">!</span>
              )}
            </button>
            <button onClick={handleToggleVisibility} className="visibility-button" disabled={isTogglingVisibility}>
              {item.active ? 'Ocultar' : 'Mostrar'}
            </button>
            <button onClick={onDelete} className="delete-button">
              Arxivar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MerxandatgeItem;
